// Host contract: route matches `/admin/integrations/<kind>` (or sets
// `root._genericIntKindOverride`); `root._integrationHub` populated by
// `loadIntegrationCatalogue`; `root._genericIntFormOpen` toggles the form.
import {
  saveIntegration,
  deleteIntegration,
  callCapability,
  reorderIntegrations
} from '../integrations/index.js'
import { slugify } from '../functions/slugify.js'

const _DRAFT_KEY = '_genericIntDraft'

const _kindFromRoot = (root) => {
  const override = root?._genericIntKindOverride
  if (typeof override === 'string' && override) return override
  const m = String(root?.currentPath || '').match(/^\/admin\/integrations\/([^?#/]+)/)
  return m ? m[1] : null
}

const _kindRow = (root) => {
  const kind = _kindFromRoot(root)
  if (!kind) return null
  return (root?._integrationHub?.kinds || []).find((k) => k.kind === kind) || null
}

// ui_flow hint column (migration 0074) — tells the form which variant
// UX to render. Today the column is populated only for registrar rows
// (backfill) but no registrar rows exist in `integration_kinds` yet,
// so every form instance evaluates to 'credential'. Wiring lives
// here so MARKETPLACE's future `MarketplaceRegistrarDomainPicker` +
// the OAuth2 connect button can drop in without touching save/delete
// logic. See NEEDED_FOR_MARKETPLACE.md § REQUEST tick-3.
const _uiFlow = (root) => {
  const kr = _kindRow(root)
  return kr?.ui_flow || 'credential'
}

// Toast helper — openNotification depends on editor-app internals and
// throws when GlobalNotification isn't fully wired (e.g. on the workspace app).
// Swallowing the throw keeps the save/delete flow intact even if the
// toast infrastructure is missing; fall back to console for diagnostics.
const _toast = (el, payload) => {
  try { el.call('openNotification', payload) }
  catch (err) { console.warn('[integration]', payload.title, payload.message || '', err) }
}

const _saveNew = async (el, s) => {
  const orgId = s.root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    _toast(el, { title: 'No active workspace', type: 'error' })
    return
  }
  const kind = _kindFromRoot(s.root)
  const kindRow = _kindRow(s.root)
  const draft = s.root?.[_DRAFT_KEY] || {}
  const fields = kindRow?.config_schema?.fields || []

  for (const f of fields) {
    if (f.required && !draft[f.key]) {
      _toast(el, {
        title: 'Missing required field',
        message: `"${f.label}" is required.`,
        type: 'error'
      })
      return
    }
  }

  const config = {}
  for (const f of fields) {
    if (draft[f.key] != null && draft[f.key] !== '') config[f.key] = draft[f.key]
  }
  const displayName = draft.displayName || config.display_name || kind

  const slug = kindRow?.allows_multiple
    ? slugify(draft.slug || draft.displayName || kind)
    : 'default'

  const { scopeType, scopeId } = el.call('resolveScope', draft, kindRow?.default_scope || 'org')
  if ((scopeType === 'workspace' || scopeType === 'project') && !scopeId) {
    _toast(el, {
      title: 'Scope ID required',
      message: `Select or enter a ${scopeType} ID.`,
      type: 'error'
    })
    return
  }

  const { ok, body } = await saveIntegration({
    orgId, kind, slug, displayName, config,
    secret: draft.secret || undefined,
    scopeType, scopeId, enabled: true
  })

  if (ok) {
    s.root.update({ [_DRAFT_KEY]: null, _genericIntFormOpen: false })
    // Reload the catalogue so the new instance appears in the list.
    // Host page can bolt on a `loadCatalogue` function; try el.call so
    // the form doesn't hard-couple to a specific loader name.
    try { await el.call('reloadIntegrationHub') } catch {}
    _toast(el, { title: `Connected ${displayName}`, type: 'success' })
  } else {
    _toast(el, {
      title: 'Connect failed',
      message: body?.error || 'Unknown error',
      type: 'error'
    })
  }
}

const _deleteInstance = async (el, s, slug, scopeType, scopeId) => {
  const orgId = s.root?.jwtClaims?.workspace_org_id
  if (!orgId) return
  const kind = _kindFromRoot(s.root)
  const { ok, body } = await deleteIntegration({ orgId, kind, slug, scopeType, scopeId })
  if (ok) {
    try { await el.call('reloadIntegrationHub') } catch {}
    _toast(el, { title: 'Disconnected', type: 'success' })
  } else {
    _toast(el, {
      title: 'Remove failed',
      message: body?.error || 'Server error',
      type: 'error'
    })
  }
}

// ── §I12 Instance reorder helpers ───────────────────────────────────
//
// HTML5 drag-and-drop reorder for GenericIntInstanceCard lists.
// We store the drag source slug on root._genericIntDragSlug so the
// dragover / drop handlers can read it regardless of which nested
// element fires the event.
//
// On drop we:
//   1. Reorder root._integrationHub.integrations in-memory (instant UI).
//   2. Derive the new slug order for the affected (kind, scope) subset.
//   3. Call reorderIntegrations() to persist positions server-side.

const _instancesForKindScope = (root) => {
  const kind = _kindFromRoot(root)
  if (!kind) return []
  return (root?._integrationHub?.integrations || []).filter((i) => i.kind === kind)
}

const _reorderInHub = (root, kind, fromSlug, toSlug) => {
  if (fromSlug === toSlug) return
  const all = root?._integrationHub?.integrations || []
  const kindItems = all.filter((i) => i.kind === kind)
  const others = all.filter((i) => i.kind !== kind)

  const fromIdx = kindItems.findIndex((i) => i.slug === fromSlug)
  const toIdx = kindItems.findIndex((i) => i.slug === toSlug)
  if (fromIdx < 0 || toIdx < 0) return

  const reordered = [...kindItems]
  const [moved] = reordered.splice(fromIdx, 1)
  reordered.splice(toIdx, 0, moved)

  root.update({
    _integrationHub: {
      ...root._integrationHub,
      integrations: [...others, ...reordered]
    },
    _genericIntDragSlug: null,
    _genericIntDragOverSlug: null
  })
}

const _persistReorder = async (el, s) => {
  const orgId = s.root?.jwtClaims?.workspace_org_id
  if (!orgId) return
  const kind = _kindFromRoot(s.root)
  if (!kind) return
  const items = _instancesForKindScope(s.root)
  if (items.length < 2) return
  const kindRow = _kindRow(s.root)
  const scopeType = items[0]?.scope_type || kindRow?.default_scope || 'org'
  const scopeId = items[0]?.scope_id || null
  const slugs = items.map((i) => i.slug)
  const { ok, body } = await reorderIntegrations({ orgId, kind, slugs, scopeType, scopeId })
  if (!ok) {
    _toast(el, {
      title: 'Reorder save failed',
      message: body?.error || 'Unknown error',
      type: 'error'
    })
  }
}

// ── New-instance form ────────────────────────────────────────────────

export const GenericIntNewForm = {
  hide: (el, s) => !s.root?._genericIntFormOpen,
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',

  FormTitle: {
    tag: 'h3',
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: (el, s) => {
      const kr = _kindRow(s.root)
      return `Connect${kr?.label ? ` ${kr.label}` : ''}`
    }
  },

  DisplayName: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => !_kindRow(s.root)?.allows_multiple,
    Label: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: 'Display name'
    },
    Input: {
      tag: 'input',
      type: 'text',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: (el, s) => _kindRow(s.root)?.label || 'My integration',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.displayName || '',
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), displayName: e.target.value }
        })
      }
    }
  },

  ConfigFields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'GenericIntConfigField',
    childrenAs: 'state',
    children: (el, s) => {
      const fields = _kindRow(s.root)?.config_schema?.fields || []
      return fields.filter((f) => f.key !== 'display_name')
    }
  },

  IntegrationScopePicker: {
    draftKey: _DRAFT_KEY,
    defaultScopeType: (el, s) => _kindRow(s.root)?.default_scope || 'org'
  },

  SecretField: {
    flow: 'y',
    gap: 'V',
    // Hide when:
    //   - no kind row yet,
    //   - auth_model='none' (no credential needed),
    //   - ui_flow='oauth2' (user connects via provider dialog, not a typed secret),
    //   - ui_flow='webhook' (URL is the credential, collected in ConfigFields).
    // Registrar + credential flows keep the field visible.
    hide: (el, s) => {
      const kr = _kindRow(s.root)
      if (!kr || kr.auth_model === 'none') return true
      const flow = _uiFlow(s.root)
      return flow === 'oauth2' || flow === 'webhook'
    },
    Label: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: (el, s) => _kindRow(s.root)?.secret_label || 'API key / Secret'
    },
    Hint: {
      tag: 'p',
      fontSize: 'Y1',
      color: 'caption',
      margin: '0 0 V 0',
      hide: (el, s) => !_kindRow(s.root)?.secret_hint,
      text: (el, s) => _kindRow(s.root)?.secret_hint || ''
    },
    Input: {
      tag: 'input',
      type: 'password',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: '••••••••••••••••',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.secret || '',
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), secret: e.target.value }
        })
      }
    }
  },

  // OAuth2 flow replaces the secret field — user connects via provider
  // dialog instead of typing a key. `startOAuthFlow` is a registered
  // function in `workspace/packages/workspace/functions/` (INTEGRATIONS
  // scope). Today it's a stub; wired fully when DOMAIN's Cloudflare
  // registrar / other OAuth2 kinds land. MARKETPLACE tick 4 spec.
  MarketplaceOAuthConnectButton: {
    if: (el, s) => _uiFlow(s.root) === 'oauth2'
  },

  // Registrar flow adds a domain management card AFTER the secret
  // field — user enters API key first, then uses the picker to search /
  // register domains via `sdk-bridge.callCapability`. v0 is a hint
  // card; MARKETPLACE replaces with search+results UI once DOMAIN's
  // first provider handler (porkbun.ts — shipped 2026-04-24) is wired
  // into `integration-call`'s dispatch table. MARKETPLACE tick 4 spec.
  MarketplaceRegistrarDomainPicker: {
    if: (el, s) => _uiFlow(s.root) === 'registrar'
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Y2',
      cursor: 'pointer',
      text: 'Cancel',
      onClick: (e, el, s) => {
        s.root.update({ _genericIntFormOpen: false, [_DRAFT_KEY]: null })
      }
    },

    SaveBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'blue',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      text: (el, s) => s.root?._genericIntSaving ? 'Connecting…' : 'Connect',
      onClick: async (e, el, s) => {
        s.root.update({ _genericIntSaving: true })
        await _saveNew(el, s)
        s.root.update({ _genericIntSaving: false })
      }
    }
  }
}

// ── Config field (driven by config_schema.fields) ────────────────────

export const GenericIntConfigField = {
  flow: 'y',
  gap: 'V',

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => {
      const req = s.required ? ' *' : ''
      return (s.label || s.key || '') + req
    }
  },

  Input: {
    tag: 'input',
    type: 'text',
    width: '100%',
    padding: 'Z',
    round: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => s.root?.[_DRAFT_KEY]?.[s.key] || '',
    onInput: (e, el, s) => {
      s.root.update({
        [_DRAFT_KEY]: {
          ...(s.root?.[_DRAFT_KEY] || {}),
          [s.key]: e.target.value
        }
      })
    }
  }
}

// ── Instance card ────────────────────────────────────────────────────

export const GenericIntInstanceCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: (el, s) => s.root?._genericIntDragOverSlug === s.slug ? 'blue' : 'line',
  opacity: (el, s) => s.root?._genericIntDragSlug === s.slug ? '0.4' : '1',
  // §I12 — drag-and-drop reorder
  draggable: 'true',
  onDragStart: (e, el, s) => {
    s.root.update({ _genericIntDragSlug: s.slug })
    e.dataTransfer.effectAllowed = 'move'
  },
  onDragOver: (e, el, s) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const dragSlug = s.root?._genericIntDragSlug
    if (dragSlug && dragSlug !== s.slug) {
      s.root.update({ _genericIntDragOverSlug: s.slug })
    }
  },
  onDragLeave: (e, el, s) => {
    if (s.root?._genericIntDragOverSlug === s.slug) {
      s.root.update({ _genericIntDragOverSlug: null })
    }
  },
  onDrop: async (e, el, s) => {
    e.preventDefault()
    const fromSlug = s.root?._genericIntDragSlug
    const toSlug = s.slug
    if (!fromSlug || fromSlug === toSlug) {
      s.root.update({ _genericIntDragSlug: null, _genericIntDragOverSlug: null })
      return
    }
    const kind = _kindFromRoot(s.root)
    _reorderInHub(s.root, kind, fromSlug, toSlug)
    await _persistReorder(el, s)
  },
  onDragEnd: (e, el, s) => {
    s.root.update({ _genericIntDragSlug: null, _genericIntDragOverSlug: null })
  },

  Header: {
    flow: 'x',
    align: 'center space-between',

    Left: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      DragHandle: {
        tag: 'span',
        text: '⠿',
        color: 'caption',
        fontSize: 'A',
        cursor: 'grab',
        // Prevent text selection during drag
        userSelect: 'none'
      },
      Name: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.display_name || s.slug || ''
      },
      IntegrationScopeBadge: {}
    },

    Right: {
      flow: 'x',
      gap: 'Z',
      align: 'center',

      StatusPill: {
        padding: 'V Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '700',
        background: (el, s) => (s.enabled ? 'green' : 'overlayStrong'),
        color: (el, s) => (s.enabled ? 'white' : 'caption'),
        text: (el, s) => (s.enabled ? 'Live' : 'Disabled')
      },

      TestBtn: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => {
          const caps = _kindRow(s.root)?.capabilities || []
          return caps.length === 0 || !s.enabled
        },
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'title',
        fontSize: 'Y2',
        cursor: 'pointer',
        disabled: (el, s) => !!s.root?._genericIntTesting?.[s.slug],
        text: (el, s) => s.root?._genericIntTesting?.[s.slug] ? 'Testing…' : 'Test',
        onClick: async (e, el, s) => {
          const orgId = s.root?.jwtClaims?.workspace_org_id
          if (!orgId) return
          const caps = _kindRow(s.root)?.capabilities || []
          if (!caps.length) return
          s.root.update({
            _genericIntTesting: { ...(s.root?._genericIntTesting || {}), [s.slug]: true }
          })
          const res = await callCapability(
            { orgId, kind: _kindFromRoot(s.root), slug: s.slug },
            caps[0],
            {}
          )
          s.root.update({
            _genericIntTesting: { ...(s.root?._genericIntTesting || {}), [s.slug]: false }
          })
          _toast(el, res?.error
            ? { title: 'Test failed', message: res.error, type: 'error' }
            : { title: 'Test passed ✓', type: 'success' })
        }
      },

      RemoveBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'red',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: 'Remove',
        onClick: async (e, el, s) => {
          await _deleteInstance(el, s, s.slug, s.scope_type, s.scope_id)
        }
      }
    }
  },

  ConfigPreview: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const cfg = s.config
      return !cfg || Object.keys(cfg).length === 0
    },
    children: (el, s) => {
      const cfg = s.config || {}
      return Object.entries(cfg)
        .filter(([k]) => !k.includes('secret') && !k.includes('key') && !k.includes('token'))
        .slice(0, 4)
        .map(([k, v]) => ({ key: k, value: String(v) }))
    },
    childrenAs: 'state',
    childExtends: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      Key: {
        color: 'caption',
        text: (el, s) => s.key || '',
        minWidth: '120px'
      },
      Value: {
        color: 'title',
        text: (el, s) => s.value || '—',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    }
  }
}
